import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { api, errMsg } from "@/lib/api";
import { Loading, EmptyState, scoreTone, fmtDate, GradeBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Copy, Plus, Users, ClipboardList, Trash2, CalendarDays, Search, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AssignDialog = ({ open, onOpenChange, classId, onDone }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [due, setDue] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get("/lessons/search", { params: { q } }).then(({ data }) => setResults(data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const save = async () => {
    if (!picked) return toast.error("Pick a lesson to assign");
    setBusy(true);
    try {
      await api.post(`/classes/${classId}/assignments`, { lesson_id: picked.lesson_id, due_date: due ? format(due, "yyyy-MM-dd") : null, note });
      toast.success("Lesson assigned");
      setPicked(null); setDue(null); setNote(""); setQ("");
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-xl" data-testid="assign-dialog">
        <DialogHeader><DialogTitle className="font-display">Assign a lesson</DialogTitle><DialogDescription>Search the library, your courses and your AI lessons.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lessons by title" className="pl-9 h-11" data-testid="assign-search-input" />
          </div>
          <div className="max-h-56 overflow-y-auto border border-border rounded-lg divide-y divide-border">
            {results.length === 0 ? <p className="p-3 text-sm text-muted-foreground">No lessons found.</p> : results.map((l) => (
              <button key={l.lesson_id} onClick={() => setPicked(l)} className={`w-full text-left px-3 py-2.5 flex items-center gap-2 text-sm ${picked?.lesson_id === l.lesson_id ? "bg-[#D7F3F1]" : "hover:bg-secondary"}`} data-testid="assign-lesson-option">
                {picked?.lesson_id === l.lesson_id && <CheckCircle2 className="h-4 w-4 text-[#0F7B7D]" />}
                <span className="flex-1 truncate">{l.title}</span>
                <GradeBadge grade={l.grade_band} />
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Due date (optional)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-11 justify-start bg-white font-normal" data-testid="assign-due-date-button"><CalendarDays className="h-4 w-4 mr-2" />{due ? format(due, "MMM d, yyyy") : "Pick a date"}</Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start"><Calendar mode="single" selected={due} onSelect={setDue} disabled={(d) => d < new Date(new Date().toDateString())} /></PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2"><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="h-11" data-testid="assign-note-input" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} disabled={busy || !picked} data-testid="assign-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Assign</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ClassDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get(`/classes/${id}`).then(({ data }) => setC(data)).catch((e) => { toast.error(errMsg(e)); navigate("/dashboard"); }), [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!c) return <Loading />;

  const removeStudent = async (sid) => {
    try { await api.delete(`/classes/${id}/students/${sid}`); toast.success("Student removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const removeAssignment = async (aid) => {
    try { await api.delete(`/assignments/${aid}`); toast.success("Assignment removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteClass = async () => {
    try { await api.delete(`/classes/${id}`); toast.success("Class deleted"); navigate("/dashboard"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <Button variant="ghost" asChild className="mb-4 -ml-3"><Link to="/dashboard"><ArrowLeft className="h-4 w-4 mr-1" />Dashboard</Link></Button>
      <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6 fade-up">
        <div className="flex-1">
          <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">Class</div>
          <h1 className="mt-2 text-3xl font-display font-semibold" data-testid="class-title">{c.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">{c.grade_band || "Mixed grades"} · {c.students.length} students · {c.assignments.length} assignments</div>
        </div>
        <div className="bg-[#FFF1C7] border border-[#F2B705]/30 rounded-[14px] px-5 py-4 flex items-center gap-4">
          <div>
            <div className="text-xs text-[#5B4A10]">Students join with code</div>
            <div className="font-display text-3xl font-semibold tracking-[0.2em]" data-testid="class-join-code">{c.join_code}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard?.writeText(c.join_code); toast.success("Join code copied"); }} aria-label="Copy code" data-testid="class-copy-code-button"><Copy className="h-4 w-4" /></Button>
        </div>
      </div>

      <Tabs defaultValue="gradebook" className="mt-8">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <TabsList className="bg-secondary">
            <TabsTrigger value="gradebook" data-testid="class-tab-gradebook">Gradebook</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="class-tab-assignments">Assignments</TabsTrigger>
            <TabsTrigger value="roster" data-testid="class-tab-roster">Roster</TabsTrigger>
          </TabsList>
          <Button className="ml-auto" onClick={() => setAssignOpen(true)} data-testid="class-assign-button"><Plus className="h-4 w-4 mr-2" />Assign lesson</Button>
        </div>

        <TabsContent value="gradebook">
          {c.students.length === 0 || c.assignments.length === 0 ? (
            <EmptyState icon={ClipboardList} title="Gradebook fills in automatically" text="Once students join and you assign lessons, their best quiz scores show up here." />
          ) : (
            <div className="bg-white border border-border rounded-[14px] overflow-x-auto">
              <Table data-testid="teacher-gradebook-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[160px]">Student</TableHead>
                    {c.assignments.map((a) => <TableHead key={a.assignment_id} className="min-w-[140px]">{a.lesson_title}</TableHead>)}
                    <TableHead className="text-right">Average</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {c.students.map((s) => (
                    <TableRow key={s.user_id}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      {c.assignments.map((a) => {
                        const g = s.grades[a.assignment_id];
                        return <TableCell key={a.assignment_id} className={g?.best_percent != null ? `font-display font-semibold ${scoreTone(g.best_percent)}` : "text-muted-foreground"}>{g?.best_percent != null ? `${g.best_percent}%` : g?.completed ? "Read" : "—"}</TableCell>;
                      })}
                      <TableCell className={`text-right font-display font-semibold ${scoreTone(s.average_score)}`}>{s.average_score != null ? `${s.average_score}%` : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="assignments">
          {c.assignments.length === 0 ? (
            <EmptyState icon={ClipboardList} title="No assignments yet" action={<Button onClick={() => setAssignOpen(true)}>Assign a lesson</Button>} />
          ) : (
            <div className="space-y-3" data-testid="class-assignments-list">
              {c.assignments.map((a) => (
                <div key={a.assignment_id} className="bg-white border border-border rounded-[14px] p-4 flex items-center gap-4" data-testid="class-assignment-item">
                  <div className="flex-1 min-w-0">
                    <Link to={`/lessons/${a.lesson_id}`} className="font-medium hover:underline">{a.lesson_title}</Link>
                    <div className="text-xs text-muted-foreground">{a.subject}{a.due_date ? ` · due ${fmtDate(a.due_date)}` : ""}{a.note ? ` · ${a.note}` : ""}</div>
                  </div>
                  <div className="text-sm text-muted-foreground whitespace-nowrap">{a.completed_count}/{c.students.length} done</div>
                  <Button variant="ghost" size="icon" onClick={() => removeAssignment(a.assignment_id)} aria-label="Remove assignment" data-testid="assignment-delete-button"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="roster">
          {c.students.length === 0 ? (
            <EmptyState icon={Users} title="No students yet" text={`Share the join code ${c.join_code} — students enter it on their dashboard.`} />
          ) : (
            <div className="bg-white border border-border rounded-[14px] overflow-x-auto">
              <Table data-testid="class-roster-table">
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Grade</TableHead><TableHead>XP</TableHead><TableHead>Lessons done</TableHead><TableHead>Avg score</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {c.students.map((s) => (
                    <TableRow key={s.user_id}>
                      <TableCell><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.email}</div></TableCell>
                      <TableCell>{s.grade_band}</TableCell>
                      <TableCell>{s.xp}</TableCell>
                      <TableCell>{s.lessons_completed}</TableCell>
                      <TableCell className={scoreTone(s.average_score)}>{s.average_score != null ? `${s.average_score}%` : "—"}</TableCell>
                      <TableCell className="text-right whitespace-nowrap"><Button variant="ghost" size="sm" asChild data-testid="roster-records-link"><Link to={`/records/${s.user_id}`}>Records</Link></Button><Button variant="ghost" size="sm" onClick={() => removeStudent(s.user_id)} data-testid="roster-remove-button">Remove</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="mt-10">
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="ghost" className="text-destructive" data-testid="class-delete-button"><Trash2 className="h-4 w-4 mr-2" />Delete class</Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete this class?</AlertDialogTitle><AlertDialogDescription>Students and their progress stay, but the class and its assignments are removed.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={deleteClass} data-testid="class-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <AssignDialog open={assignOpen} onOpenChange={setAssignOpen} classId={id} onDone={() => { setAssignOpen(false); load(); }} />
    </div>
  );
}
